'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import EmailAuthModal from './EmailAuthModal';

interface Comment {
  id: string;
  news_post_id: string | null;
  video_id: string;
  parent_id: string | null;
  author: string;
  body: string;
  likes: number;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  is_deleted: boolean;
  user_id: string | null;
  avatar_url: string | null;
  commenter_email: string | null;
  moderation_status: string;
  flagged_reason: string | null;
  replies?: Comment[];
}

interface NewsCommentsProps {
  newsPostId: string;
}

function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Avatar({ name, avatarUrl, size = 8 }: { name: string; avatarUrl?: string | null; size?: number }) {
  const [imgError, setImgError] = useState(false);
  const initial = (name || 'A')[0].toUpperCase();
  const sizeClass = `w-${size} h-${size}`;

  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt={`${name}'s avatar`}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0 border border-stroke-primary`}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div
      className={`${sizeClass} rounded-full bg-brand-red flex items-center justify-center text-white font-bold flex-shrink-0 border border-stroke-primary`}
      style={{ fontSize: size <= 6 ? '10px' : size <= 8 ? '12px' : '14px' }}
    >
      {initial}
    </div>
  );
}

export default function NewsComments({ newsPostId }: NewsCommentsProps) {
  const supabase = createClient();
  const { user, profile } = useAuth();

  const [comments, setComments] = useState<Comment[]>([]);
  const [totalComments, setTotalComments] = useState(0);
  const [loadingComments, setLoadingComments] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Reply state
  const [replyingTo, setReplyingTo] = useState<{ id: string; author: string } | null>(null);
  const [replyText, setReplyText] = useState('');
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Flag/report state
  const [flaggingId, setFlaggingId] = useState<string | null>(null);
  const [flagReasonInput, setFlagReasonInput] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flaggedIds, setFlaggedIds] = useState<Set<string>>(new Set());

  // Comment likes
  const [commentLikes, setCommentLikes] = useState<Record<string, { count: number; liked: boolean }>>({});
  const [commentLikeLoading, setCommentLikeLoading] = useState<string | null>(null);

  const buildThreaded = useCallback((rows: Comment[]): Comment[] => {
    const topLevel = rows.filter((c) => !c.parent_id && !c.is_deleted);
    const replies = rows.filter((c) => c.parent_id);
    return topLevel.map((c) => ({
      ...c,
      replies: replies
        .filter((r) => r.parent_id === c.id)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    }));
  }, []);

  const fetchCommentLikes = useCallback(
    async (commentIds: string[]) => {
      if (commentIds.length === 0) return;
      const { data: counts } = await supabase
        .from('comment_likes')
        .select('comment_id')
        .in('comment_id', commentIds);

      const countMap: Record<string, number> = {};
      (counts || []).forEach((row: { comment_id: string }) => {
        countMap[row.comment_id] = (countMap[row.comment_id] || 0) + 1;
      });

      let likedSet = new Set<string>();
      if (user) {
        const { data: userLikes } = await supabase
          .from('comment_likes')
          .select('comment_id')
          .in('comment_id', commentIds)
          .eq('user_id', user.id);
        likedSet = new Set((userLikes || []).map((r: { comment_id: string }) => r.comment_id));
      }

      setCommentLikes((prev) => {
        const next = { ...prev };
        commentIds.forEach((id) => {
          next[id] = { count: countMap[id] ?? 0, liked: likedSet.has(id) };
        });
        return next;
      });
    },
    [user, supabase]
  );

  const fetchComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const { data, error } = await supabase
        .from('comments')
        .select(
          'id, news_post_id, video_id, parent_id, author, body, likes, created_at, updated_at, deleted_at, is_deleted, user_id, avatar_url, commenter_email, moderation_status, flagged_reason'
        )
        .eq('news_post_id', newsPostId)
        .in('moderation_status', ['approved', 'pending'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Failed to load comments:', error.message);
        return;
      }

      const rows: Comment[] = (data || []).map((r: Comment) => ({ ...r, replies: [] }));
      const threaded = buildThreaded(rows);
      setComments(threaded);
      setTotalComments(rows.filter((r) => !r.is_deleted).length);

      const allIds = rows.map((c) => c.id);
      fetchCommentLikes(allIds);
    } finally {
      setLoadingComments(false);
    }
  }, [newsPostId, buildThreaded, fetchCommentLikes, supabase]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel(`comments_news_${newsPostId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'comments' },
        (payload) => {
          const newComment = payload.new as Comment;
          if (newComment.news_post_id !== newsPostId) return;
          if (newComment.is_deleted) return;
          setComments((prev) => {
            if (newComment.parent_id) {
              return prev.map((c) =>
                c.id === newComment.parent_id
                  ? {
                      ...c,
                      replies: [
                        ...(c.replies || []).filter((r) => r.id !== newComment.id),
                        { ...newComment, replies: [] },
                      ],
                    }
                  : c
              );
            } else {
              if (prev.find((c) => c.id === newComment.id)) return prev;
              return [{ ...newComment, replies: [] }, ...prev];
            }
          });
          setTotalComments((prev) => prev + 1);
          setCommentLikes((prev) => ({ ...prev, [newComment.id]: { count: 0, liked: false } }));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'comments' },
        (payload) => {
          const updated = payload.new as Comment;
          if (updated.news_post_id !== newsPostId) return;
          setComments((prev) =>
            prev.map((c) => {
              if (c.id === updated.id) return { ...c, ...updated, replies: c.replies };
              return {
                ...c,
                replies: (c.replies || []).map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
              };
            })
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [newsPostId, supabase]);

  useEffect(() => {
    if (replyingTo && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingTo]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const authorName = profile?.full_name || user.email?.split('@')[0] || 'Anonymous';
    const avatarUrl = profile?.avatar_url || null;

    const { data, error } = await supabase
      .from('comments')
      .insert({
        news_post_id: newsPostId,
        video_id: newsPostId, // required NOT NULL field — use newsPostId as placeholder
        parent_id: null,
        author: authorName,
        body: commentText.trim(),
        user_id: user.id,
        commenter_email: user.email,
        avatar_url: avatarUrl,
        moderation_status: 'pending',
      })
      .select()
      .single();

    if (error) {
      setSubmitError('Failed to post comment. Please try again.');
    } else {
      setCommentText('');
      const newComment: Comment = { ...data, replies: [] };
      setComments((prev) => {
        if (prev.find((c) => c.id === newComment.id)) return prev;
        return [newComment, ...prev];
      });
      setTotalComments((prev) => prev + 1);
      setCommentLikes((prev) => ({ ...prev, [data.id]: { count: 0, liked: false } }));
    }
    setSubmitting(false);
  };

  const handleSubmitReply = async (e: React.FormEvent, parentId: string) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const authorName = profile?.full_name || user.email?.split('@')[0] || 'Anonymous';
    const avatarUrl = profile?.avatar_url || null;

    const { data, error } = await supabase
      .from('comments')
      .insert({
        news_post_id: newsPostId,
        video_id: newsPostId,
        parent_id: parentId,
        author: authorName,
        body: replyText.trim(),
        user_id: user.id,
        commenter_email: user.email,
        avatar_url: avatarUrl,
        moderation_status: 'pending',
      })
      .select()
      .single();

    if (error) {
      setSubmitError('Failed to post reply. Please try again.');
    } else {
      const newReply: Comment = { ...data, replies: [] };
      setComments((prev) =>
        prev.map((c) =>
          c.id === parentId
            ? { ...c, replies: [...(c.replies || []).filter((r) => r.id !== newReply.id), newReply] }
            : c
        )
      );
      setTotalComments((prev) => prev + 1);
      setCommentLikes((prev) => ({ ...prev, [data.id]: { count: 0, liked: false } }));
      setReplyText('');
      setReplyingTo(null);
      setExpandedReplies((prev) => new Set([...prev, parentId]));
    }
    setSubmitting(false);
  };

  const handleDeleteComment = async (commentId: string, parentId: string | null) => {
    if (!user) return;
    setDeletingId(commentId);
    const { error } = await supabase
      .from('comments')
      .update({ is_deleted: true, deleted_at: new Date().toISOString() })
      .eq('id', commentId)
      .eq('user_id', user.id);

    if (!error) {
      if (parentId) {
        setComments((prev) =>
          prev.map((c) =>
            c.id === parentId
              ? { ...c, replies: (c.replies || []).filter((r) => r.id !== commentId) }
              : c
          )
        );
      } else {
        const target = comments.find((c) => c.id === commentId);
        const replyCount = target?.replies?.length ?? 0;
        setComments((prev) => prev.filter((c) => c.id !== commentId));
        setTotalComments((prev) => Math.max(0, prev - 1 - replyCount));
      }
      setTotalComments((prev) => Math.max(0, prev - 1));
    }
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  const handleSaveEdit = async (commentId: string, parentId: string | null) => {
    if (!user || !editText.trim()) return;
    setSavingEdit(true);
    const { error } = await supabase
      .from('comments')
      .update({ body: editText.trim(), updated_at: new Date().toISOString() })
      .eq('id', commentId)
      .eq('user_id', user.id);

    if (!error) {
      const applyEdit = (c: Comment): Comment => ({
        ...c,
        body: editText.trim(),
        updated_at: new Date().toISOString(),
      });
      if (parentId) {
        setComments((prev) =>
          prev.map((c) =>
            c.id === parentId
              ? { ...c, replies: (c.replies || []).map((r) => (r.id === commentId ? applyEdit(r) : r)) }
              : c
          )
        );
      } else {
        setComments((prev) =>
          prev.map((c) => (c.id === commentId ? { ...applyEdit(c), replies: c.replies } : c))
        );
      }
      setEditingId(null);
      setEditText('');
    }
    setSavingEdit(false);
  };

  const handleCommentLike = async (commentId: string) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (commentLikeLoading === commentId) return;
    setCommentLikeLoading(commentId);
    const current = commentLikes[commentId] ?? { count: 0, liked: false };
    try {
      if (current.liked) {
        await supabase
          .from('comment_likes')
          .delete()
          .eq('comment_id', commentId)
          .eq('user_id', user.id);
        setCommentLikes((prev) => ({
          ...prev,
          [commentId]: { count: Math.max(0, (prev[commentId]?.count ?? 1) - 1), liked: false },
        }));
      } else {
        await supabase.from('comment_likes').insert({ comment_id: commentId, user_id: user.id });
        setCommentLikes((prev) => ({
          ...prev,
          [commentId]: { count: (prev[commentId]?.count ?? 0) + 1, liked: true },
        }));
      }
    } finally {
      setCommentLikeLoading(null);
    }
  };

  const handleFlagComment = async (commentId: string) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!flagReasonInput.trim()) return;
    setFlagSubmitting(true);
    const { error } = await supabase
      .from('comments')
      .update({
        moderation_status: 'flagged',
        flagged_reason: flagReasonInput.trim(),
      })
      .eq('id', commentId);

    if (!error) {
      setFlaggedIds((prev) => new Set([...prev, commentId]));
      setFlaggingId(null);
      setFlagReasonInput('');
    }
    setFlagSubmitting(false);
  };

  const toggleReplies = (commentId: string) => {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  };

  const CommentActions = ({
    comment,
    parentId,
    isReply = false,
  }: {
    comment: Comment;
    parentId: string | null;
    isReply?: boolean;
  }) => {
    const cLike = commentLikes[comment.id] ?? { count: 0, liked: false };
    const isOwner = user && comment.user_id === user.id;
    const isFlagged = flaggedIds.has(comment.id) || comment.moderation_status === 'flagged';

    return (
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        {/* Like */}
        <button
          onClick={() => handleCommentLike(comment.id)}
          disabled={commentLikeLoading === comment.id}
          className={`flex items-center gap-1 text-xs transition-colors ${
            cLike.liked ? 'text-brand-red' : 'text-text-muted hover:text-brand-red'
          } disabled:opacity-60`}
          aria-label={cLike.liked ? 'Unlike' : 'Like'}
        >
          <svg
            className={`${isReply ? 'w-3 h-3' : 'w-3.5 h-3.5'}`}
            fill={cLike.liked ? 'currentColor' : 'none'}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
            />
          </svg>
          {cLike.count > 0 && <span>{cLike.count}</span>}
        </button>

        {/* Reply (only on top-level) */}
        {!isReply && (
          <button
            onClick={() => {
              if (replyingTo?.id === comment.id) {
                setReplyingTo(null);
                setReplyText('');
              } else {
                setReplyingTo({ id: comment.id, author: comment.author });
                setReplyText('');
              }
            }}
            className="text-text-muted hover:text-brand-red text-xs transition-colors font-medium"
          >
            Reply
          </button>
        )}

        {/* Toggle replies */}
        {!isReply && comment.replies && comment.replies.length > 0 && (
          <button
            onClick={() => toggleReplies(comment.id)}
            className="text-brand-red hover:text-red-400 text-xs transition-colors font-medium"
          >
            {expandedReplies.has(comment.id)
              ? '▲ Hide replies'
              : `▼ ${comment.replies.length} ${comment.replies.length === 1 ? 'reply' : 'replies'}`}
          </button>
        )}

        {/* Flag / Report (non-owners only) */}
        {!isOwner && !isFlagged && (
          <button
            onClick={() => {
              if (!user) { setShowAuthModal(true); return; }
              setFlaggingId(flaggingId === comment.id ? null : comment.id);
              setFlagReasonInput('');
            }}
            className="flex items-center gap-1 text-text-muted hover:text-yellow-500 text-xs transition-colors ml-auto"
            aria-label="Report comment"
            title="Report this comment"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
              />
            </svg>
            <span className="hidden sm:inline">Report</span>
          </button>
        )}

        {isFlagged && !isOwner && (
          <span className="flex items-center gap-1 text-yellow-500 text-xs ml-auto">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
            </svg>
            Reported
          </span>
        )}

        {/* Owner actions */}
        {isOwner && (
          <span className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => {
                setEditingId(comment.id);
                setEditText(comment.body);
              }}
              className="flex items-center gap-1 text-text-muted hover:text-brand-red text-xs transition-colors"
              aria-label="Edit"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Edit
            </button>
            {confirmDeleteId === comment.id ? (
              <span className="flex items-center gap-1.5">
                <span className="text-text-muted text-xs">Delete?</span>
                <button
                  onClick={() => handleDeleteComment(comment.id, parentId)}
                  disabled={deletingId === comment.id}
                  className="text-red-500 hover:text-red-400 text-xs font-bold transition-colors disabled:opacity-40"
                >
                  {deletingId === comment.id ? 'Deleting…' : 'Yes'}
                </button>
                <button
                  onClick={() => setConfirmDeleteId(null)}
                  className="text-text-muted hover:text-text-secondary text-xs transition-colors"
                >
                  No
                </button>
              </span>
            ) : (
              <button
                onClick={() => setConfirmDeleteId(comment.id)}
                className="flex items-center gap-1 text-text-muted hover:text-red-500 text-xs transition-colors"
                aria-label="Delete"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Delete
              </button>
            )}
          </span>
        )}
      </div>
    );
  };

  const CommentItem = ({
    comment,
    parentId = null,
    isReply = false,
  }: {
    comment: Comment;
    parentId?: string | null;
    isReply?: boolean;
  }) => (
    <div className={`flex gap-3`}>
      <Avatar name={comment.author} avatarUrl={comment.avatar_url} size={isReply ? 6 : 8} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`text-text-primary font-semibold ${isReply ? 'text-xs' : 'text-sm'}`}>
            {comment.author}
          </span>
          {user && comment.user_id === user.id && (
            <span className="text-brand-red text-xs font-bold uppercase tracking-wide">You</span>
          )}
          <span className="text-text-muted text-xs">{formatTimestamp(comment.created_at)}</span>
          {comment.updated_at && !comment.is_deleted && (
            <span className="text-text-muted text-xs italic">(edited)</span>
          )}
          {comment.moderation_status === 'pending' && (
            <span className="text-yellow-500 text-[10px] font-bold uppercase tracking-wide bg-yellow-500/10 px-1.5 py-0.5 rounded-[2px]">
              Pending review
            </span>
          )}
        </div>

        {editingId === comment.id ? (
          <div className="space-y-2 mt-1">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              maxLength={1000}
              rows={2}
              autoFocus
              className="w-full bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2 text-text-primary text-sm resize-none focus:outline-none focus:border-brand-red transition-colors"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSaveEdit(comment.id, parentId)}
                disabled={!editText.trim() || savingEdit}
                className="bg-brand-red text-white text-xs font-bold uppercase px-3 py-1.5 rounded-[4px] hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {savingEdit ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setEditingId(null);
                  setEditText('');
                }}
                className="text-text-muted hover:text-text-secondary text-xs transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p
            className={`leading-relaxed break-words ${isReply ? 'text-xs' : 'text-sm'} ${
              comment.is_deleted ? 'text-text-muted italic' : 'text-text-secondary'
            }`}
          >
            {comment.is_deleted ? '[deleted]' : comment.body}
          </p>
        )}

        {!comment.is_deleted && editingId !== comment.id && (
          <CommentActions comment={comment} parentId={parentId} isReply={isReply} />
        )}

        {/* Flag/Report form */}
        {flaggingId === comment.id && !comment.is_deleted && (
          <div className="mt-3 bg-surface-tertiary border border-yellow-500/30 rounded-[4px] p-3 space-y-2">
            <p className="text-text-secondary text-xs font-semibold">Report this comment</p>
            <select
              value={flagReasonInput}
              onChange={(e) => setFlagReasonInput(e.target.value)}
              className="w-full bg-surface-secondary border border-stroke-primary rounded-[4px] px-3 py-2 text-text-primary text-xs focus:outline-none focus:border-brand-red transition-colors"
            >
              <option value="">Select a reason…</option>
              <option value="spam">Spam or advertising</option>
              <option value="harassment">Harassment or bullying</option>
              <option value="hate_speech">Hate speech</option>
              <option value="misinformation">Misinformation</option>
              <option value="inappropriate">Inappropriate content</option>
              <option value="other">Other</option>
            </select>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleFlagComment(comment.id)}
                disabled={!flagReasonInput || flagSubmitting}
                className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-bold uppercase px-3 py-1.5 rounded-[4px] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {flagSubmitting ? 'Reporting…' : 'Submit Report'}
              </button>
              <button
                onClick={() => {
                  setFlaggingId(null);
                  setFlagReasonInput('');
                }}
                className="text-text-muted hover:text-text-secondary text-xs transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Reply form */}
        {!isReply && replyingTo?.id === comment.id && (
          <form onSubmit={(e) => handleSubmitReply(e, comment.id)} className="mt-3">
            <div className="flex gap-2 items-start">
              <Avatar
                name={profile?.full_name || user?.email || 'A'}
                avatarUrl={profile?.avatar_url}
                size={6}
              />
              <div className="flex-1 space-y-2">
                <textarea
                  ref={replyInputRef}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={`Reply to ${replyingTo.author}…`}
                  maxLength={500}
                  rows={2}
                  className="w-full bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2 text-text-primary text-xs placeholder-text-muted resize-none focus:outline-none focus:border-brand-red transition-colors"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={!replyText.trim() || submitting}
                    className="bg-brand-red text-white text-xs font-bold uppercase px-3 py-1.5 rounded-[4px] hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? 'Posting…' : 'Post Reply'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyText('');
                    }}
                    className="text-text-muted hover:text-text-secondary text-xs transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

        {/* Nested replies */}
        {!isReply && comment.replies && comment.replies.length > 0 && expandedReplies.has(comment.id) && (
          <ul className="mt-3 space-y-3 border-l-2 border-stroke-primary pl-4">
            {comment.replies.map((reply) => (
              <li key={reply.id}>
                <CommentItem comment={reply} parentId={comment.id} isReply />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <div className="mt-10 pt-8 border-t border-stroke-primary">
      <h2 className="text-xl font-bold text-text-primary mb-5 flex items-center gap-2">
        <svg className="w-5 h-5 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        Comments
        {totalComments > 0 && (
          <span className="text-text-muted text-sm font-normal">({totalComments})</span>
        )}
        <span className="ml-auto flex items-center gap-1 text-xs text-green-500 font-normal">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block"></span>
          Live
        </span>
      </h2>

      {/* Comment form */}
      <div className="mb-6">
        {user ? (
          <form onSubmit={handleSubmitComment} className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Avatar
                name={profile?.full_name || user.email || 'A'}
                avatarUrl={profile?.avatar_url}
                size={8}
              />
              <span className="text-text-secondary text-sm">
                Commenting as{' '}
                <span className="text-text-primary font-medium">
                  {profile?.full_name || user.email?.split('@')[0]}
                </span>
              </span>
            </div>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Share your thoughts on this article…"
              rows={3}
              maxLength={1000}
              className="w-full bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2.5 text-text-primary placeholder-text-muted text-sm resize-none focus:outline-none focus:border-brand-red transition-colors"
            />
            {submitError && <p className="text-red-500 text-sm">{submitError}</p>}
            <div className="flex items-center justify-between">
              <span className="text-text-muted text-xs">{commentText.length}/1000</span>
              <button
                type="submit"
                disabled={submitting || !commentText.trim()}
                className="btn-primary text-sm px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Posting…
                  </span>
                ) : (
                  'Post Comment'
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-text-primary text-sm font-medium">Join the conversation</p>
              <p className="text-text-muted text-xs mt-0.5">Sign in to comment, reply, and like</p>
            </div>
            <button
              onClick={() => setShowAuthModal(true)}
              className="btn-primary text-sm px-4 py-2 flex-shrink-0"
            >
              Sign In to Comment
            </button>
          </div>
        )}
      </div>

      {/* Comments list */}
      {loadingComments ? (
        <div className="flex items-center gap-2 text-text-muted py-4">
          <div className="w-4 h-4 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Loading comments…</span>
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="w-10 h-10 text-text-muted mx-auto mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <p className="text-text-muted text-sm">No comments yet. Be the first to comment!</p>
          <p className="text-text-muted text-xs mt-1">Comments are reviewed before appearing.</p>
        </div>
      ) : (
        <ul className="space-y-5">
          {comments.map((comment) => (
            <li key={comment.id} className="pb-5 border-b border-stroke-primary last:border-b-0 last:pb-0">
              <CommentItem comment={comment} parentId={null} />
            </li>
          ))}
        </ul>
      )}

      <EmailAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
        title="Sign In to Comment"
        description="Create a free account or sign in with your email to join the conversation."
      />
    </div>
  );
}
